import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import logo from "../logo.png";
import { FaEnvelope, FaPhone, FaLock, FaArrowRight, FaTimes, FaShieldAlt, FaRedo, FaCheckCircle } from 'react-icons/fa';

const AdminLogin = () => {
  const [loginMethod, setLoginMethod] = useState('email');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Forgot password states
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage('');

    try {
      const contactInfo = loginMethod === 'email' ? email : mobile;

      const response = await fetch('https://car.amar.co.jp/api/authorization/auth/admin/login', {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Accept-Language': 'en',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contactInfo,
          password
        })
      });

      const data = await response.json();

      if (response.status === 200) {
        localStorage.setItem('adminAuthToken', data.token);
        // localStorage.setItem('authToken', data.token);
        if (data.userId) localStorage.setItem('adminUserId', String(data.userId));
        if (data.roleId) localStorage.setItem('adminRoleId', String(data.roleId));
        if (data.roleName) localStorage.setItem('adminRole', data.roleName);
        
        let loggedInAdminName = 'Admin User';
        if (data.firstName) {
          loggedInAdminName = data.firstName;
          if (data.lastName) {
            loggedInAdminName = `${loggedInAdminName} ${data.lastName}`;
          }
        } else if (data.fullName) {
          loggedInAdminName = data.fullName;
        } else if (data.adminName) {
          loggedInAdminName = data.adminName;
        } else if (data.user && data.user.name) {
          loggedInAdminName = data.user.name;
        }

        localStorage.setItem('adminName', loggedInAdminName);
        setMessage('Login successful! Redirecting...');
        setTimeout(() => navigate('/adminDashBoard'), 1000);
      } else {
        setMessage(data.message || 'Invalid credentials. Please try again.');
      }
    } catch (error) {
      console.error('Login error:', error);
      setMessage('An error occurred during login. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendOtp = async () => {
    if (!forgotEmail) {
      setMessage('Please enter your email address');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(forgotEmail)) {
      setMessage('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setMessage('');

    try {
      const token = localStorage.getItem('adminAuthToken');
      
      const response = await fetch(
        `https://car.amar.co.jp/api/authorization/auth/forgot-password/send-otp?passwordResetRequestEmailId=${encodeURIComponent(forgotEmail)}`,
        {
          method: 'POST',
          headers: {
            'accept': 'application/json',
            'Accept-Language': 'en',
            'Authorization': token ? `Bearer ${token}` : '',
          },
        }
      );

      if (response.ok) {
        setOtpSent(true);
        setMessage('OTP sent to your email successfully!');
      } else {
        const errorData = await response.json();
        setMessage(errorData.message || 'Failed to send OTP. Please try again.');
      }
    } catch (error) {
      console.error('Send OTP error:', error);
      setMessage('An error occurred while sending OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      setMessage('Please enter a valid 6-digit OTP');
      return;
    }

    setIsLoading(true);
    setMessage('');

    try {
      const token = localStorage.getItem('adminAuthToken');
      
      const response = await fetch(
        'https://car.amar.co.jp/api/authorization/auth/forgot-password/verify-otp',
        {
          method: 'POST',
          headers: {
            'accept': 'application/json',
            'Accept-Language': 'en',
            'Authorization': token ? `Bearer ${token}` : '',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            contactInfo: forgotEmail,
            otp: otp
          })
        }
      );

      if (response.ok) {
        setOtpVerified(true);
        setMessage('OTP verified successfully! Now set your new password.');
      } else {
        const errorData = await response.json();
        setMessage(errorData.message || 'Invalid OTP. Please try again.');
      }
    } catch (error) {
      console.error('Verify OTP error:', error);
      setMessage('An error occurred while verifying OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || !confirmPassword) {
      setMessage('Please enter and confirm your new password');
      return;
    }

    if (newPassword.length < 6) {
      setMessage('Password must be at least 6 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage('Passwords do not match');
      return;
    }

    setIsLoading(true);
    setMessage('');

    try {
      const token = localStorage.getItem('adminAuthToken');
      
      const response = await fetch(
        'https://car.amar.co.jp/api/authorization/auth/forgot-password',
        {
          method: 'POST',
          headers: {
            'accept': 'application/json',
            'Accept-Language': 'en',
            'Authorization': token ? `Bearer ${token}` : '',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userEmailId: forgotEmail,
            newPassword: newPassword,
            confirmPassword: confirmPassword
          })
        }
      );

      if (response.ok) {
        setMessage('Password reset successfully! You can now login with your new password.');
        
        setTimeout(() => {
          setShowForgotPassword(false);
          setOtpSent(false);
          setOtpVerified(false);
          setOtp('');
          setNewPassword('');
          setConfirmPassword('');
          setForgotEmail('');
        }, 2000);
      } else {
        const errorData = await response.json();
        setMessage(errorData.message || 'Failed to reset password. Please try again.');
      }
    } catch (error) {
      console.error('Reset password error:', error);
      setMessage('An error occurred while resetting password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForgotPasswordFlow = () => {
    setShowForgotPassword(false);
    setOtpSent(false);
    setOtpVerified(false);
    setOtp('');
    setNewPassword('');
    setConfirmPassword('');
    setForgotEmail('');
    setMessage('');
  };

  const toggleLoginMethod = () => {
    setLoginMethod(loginMethod === 'email' ? 'mobile' : 'email');
    setMessage('');
  };

  return (
    <div className="admin-login-container" style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f9f9f9 0%, #ffffff 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div className="login-card" style={{
        width: '100%',
        maxWidth: '420px',
        background: 'white',
        borderRadius: '20px',
        boxShadow: '0 10px 40px rgba(211, 47, 47, 0.1), 0 0 20px rgba(211, 47, 47, 0.05)',
        overflow: 'hidden',
        position: 'relative'
      }}>
        {/* Decorative elements */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: 'linear-gradient(90deg, #d32f2f, #f44336, #ff5252)'
        }} />
        
        <div style={{
          padding: '40px 35px',
          position: 'relative'
        }}>
          {/* Logo and Header */}
          <div style={{
            textAlign: 'center',
            marginBottom: '30px'
          }}>
            <Box
              component="img"
              src={logo}
              alt="Logo"
              // onClick={() => navigate("/")}
              sx={{
                maxWidth: '230px',
                width: '100%',
                // cursor: 'pointer',
                transition: 'transform 0.3s ease',
                "&:hover": { transform: 'scale(1.05)' },
                marginBottom: '15px'
              }}
            />
            <h1 style={{
              color: '#333',
              fontSize: '28px',
              fontWeight: '700',
              marginBottom: '8px',
              background: 'linear-gradient(90deg, #d32f2f, #f44336)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              Admin Portal
            </h1>
            <p style={{
              color: '#666',
              fontSize: '14px',
              fontWeight: '400'
            }}>
              Secure access to your dashboard
            </p>
          </div>

          {message && (
            <div className="message" style={{
              padding: '12px 16px',
              backgroundColor: message.includes('success') ? 'rgba(46, 125, 50, 0.1)' : 'rgba(211, 47, 47, 0.1)',
              color: message.includes('success') ? '#2e7d32' : '#d32f2f',
              borderRadius: '10px',
              marginBottom: '20px',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              borderLeft: `4px solid ${message.includes('success') ? '#4caf50' : '#d32f2f'}`
            }}>
              {message.includes('success') ? <FaCheckCircle /> : <FaShieldAlt />}
              {message}
            </div>
          )}

          {/* Main Login Form */}
          {!showForgotPassword && (
            <form onSubmit={handleLogin}>
              {/* Login Method Toggle */}
              <div style={{
                display: 'flex',
                backgroundColor: '#f8f9fa',
                borderRadius: '12px',
                padding: '5px',
                marginBottom: '25px'
              }}>
                <button
                  type="button"
                  onClick={() => setLoginMethod('email')}
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: loginMethod === 'email' ? '#d32f2f' : 'transparent',
                    color: loginMethod === 'email' ? 'white' : '#666',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  <FaEnvelope /> Email
                </button>
                <button
                  type="button"
                  onClick={() => setLoginMethod('mobile')}
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: loginMethod === 'mobile' ? '#d32f2f' : 'transparent',
                    color: loginMethod === 'mobile' ? 'white' : '#666',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  <FaPhone /> Mobile
                </button>
              </div>

              {/* Email/Mobile Input */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '8px'
                }}>
                  {loginMethod === 'email' ? <FaEnvelope style={{ color: '#d32f2f', marginRight: '10px' }} /> 
                   : <FaPhone style={{ color: '#d32f2f', marginRight: '10px' }} />}
                  <label style={{
                    color: '#444',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}>
                    {loginMethod === 'email' ? 'Email Address' : 'Mobile Number'}
                  </label>
                </div>
                <input
                  type={loginMethod === 'email' ? 'email' : 'tel'}
                  value={loginMethod === 'email' ? email : mobile}
                  onChange={(e) => loginMethod === 'email' ? setEmail(e.target.value) : setMobile(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '10px',
                    fontSize: '15px',
                    transition: 'all 0.3s',
                    outline: 'none',
                    backgroundColor: '#fafafa'
                  }}
                  placeholder={loginMethod === 'email' ? 'admin@example.com' : '+1 (123) 456-7890'}
                  onFocus={(e) => e.target.style.borderColor = '#d32f2f'}
                  onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                />
              </div>

              {/* Password Input */}
              <div style={{ marginBottom: '25px' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '8px'
                }}>
                  <FaLock style={{ color: '#d32f2f', marginRight: '10px' }} />
                  <label style={{
                    color: '#444',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}>
                    Password
                  </label>
                </div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '10px',
                    fontSize: '15px',
                    transition: 'all 0.3s',
                    outline: 'none',
                    backgroundColor: '#fafafa'
                  }}
                  placeholder="Enter your password"
                  onFocus={(e) => e.target.style.borderColor = '#d32f2f'}
                  onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                />
              </div>

              {/* Forgot Password Link */}
              <div style={{
                textAlign: 'right',
                marginBottom: '30px'
              }}>
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: '#d32f2f',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginLeft: 'auto',
                    transition: 'all 0.3s',
                    padding: '8px'
                  }}
                  onMouseOver={(e) => e.target.style.color = '#b71c1c'}
                  onMouseOut={(e) => e.target.style.color = '#d32f2f'}
                >
                  <FaLock /> Forgot Password?
                </button>
              </div>

              {/* Login Button */}
              <button
                type="submit"
                disabled={isLoading}
                style={{
                  width: '100%',
                  padding: '16px',
                  background: 'linear-gradient(90deg, #d32f2f, #f44336)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: '700',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  boxShadow: '0 4px 15px rgba(211, 47, 47, 0.3)',
                  opacity: isLoading ? 0.7 : 1,
                  transform: 'translateY(0)'
                }}
                onMouseOver={(e) => !isLoading && (e.target.style.transform = 'translateY(-2px)')}
                onMouseOut={(e) => !isLoading && (e.target.style.transform = 'translateY(0)')}
              >
                {isLoading ? (
                  <>
                    <div style={{
                      width: '20px',
                      height: '20px',
                      border: '2px solid white',
                      borderTopColor: 'transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    Authenticating...
                  </>
                ) : (
                  <>
                    Sign In <FaArrowRight />
                  </>
                )}
              </button>
            </form>
          )}

          {/* Forgot Password Overlay */}
          {showForgotPassword && (
            <div className="forgot-password-overlay" style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: '20px',
              backdropFilter: 'blur(5px)'
            }}>
              <div style={{
                background: 'white',
                borderRadius: '20px',
                width: '100%',
                maxWidth: '450px',
                padding: '40px',
                position: 'relative',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                animation: 'slideUp 0.3s ease'
              }}>
                {/* Close Button */}
                <button
                  type="button"
                  onClick={resetForgotPasswordFlow}
                  style={{
                    position: 'absolute',
                    top: '20px',
                    right: '20px',
                    backgroundColor: '#f8f9fa',
                    border: 'none',
                    color: '#666',
                    cursor: 'pointer',
                    fontSize: '22px',
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    transition: 'all 0.3s'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = '#d32f2f';
                    e.target.style.color = 'white';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = '#f8f9fa';
                    e.target.style.color = '#666';
                  }}
                >
                  <FaTimes />
                </button>

                {/* Progress Steps */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginBottom: '30px',
                  gap: '20px'
                }}>
                  {['Email', 'OTP', 'Password'].map((step, index) => (
                    <div key={step} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        background: index === 0 || (index === 1 && otpSent) || (index === 2 && otpVerified)
                          ? 'linear-gradient(135deg, #d32f2f, #f44336)'
                          : '#f0f0f0',
                        color: index === 0 || (index === 1 && otpSent) || (index === 2 && otpVerified)
                          ? 'white'
                          : '#999',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: '700',
                        fontSize: '16px',
                        boxShadow: index === 0 || (index === 1 && otpSent) || (index === 2 && otpVerified)
                          ? '0 4px 10px rgba(211, 47, 47, 0.3)'
                          : 'none'
                      }}>
                        {index + 1}
                      </div>
                      <span style={{
                        color: index === 0 || (index === 1 && otpSent) || (index === 2 && otpVerified)
                          ? '#333'
                          : '#999',
                        fontWeight: '600',
                        fontSize: '14px'
                      }}>
                        {step}
                      </span>
                      {index < 2 && (
                        <div style={{
                          width: '20px',
                          height: '2px',
                          background: index === 0 ? 'linear-gradient(90deg, #d32f2f, #f0f0f0)' : 
                                     (index === 1 && otpSent) ? 'linear-gradient(90deg, #d32f2f, #f0f0f0)' : '#f0f0f0'
                        }} />
                      )}
                    </div>
                  ))}
                </div>

                <h2 style={{
                  color: '#333',
                  textAlign: 'center',
                  marginBottom: '10px',
                  fontSize: '24px',
                  fontWeight: '700'
                }}>
                  Reset Password
                </h2>

                <p style={{
                  color: '#666',
                  fontSize: '14px',
                  textAlign: 'center',
                  marginBottom: '30px',
                  lineHeight: '1.6'
                }}>
                  {!otpSent ? 'Enter your email to receive a verification code' :
                   !otpVerified ? 'Enter the OTP sent to your email' :
                   'Create a new password for your account'}
                </p>

                {/* Step 1: Enter Email */}
                {!otpSent && !otpVerified && (
                  <>
                    <div style={{ marginBottom: '25px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                        <FaEnvelope style={{ color: '#d32f2f', marginRight: '10px' }} />
                        <label style={{ color: '#444', fontSize: '14px', fontWeight: '600' }}>
                          Email Address
                        </label>
                      </div>
                      <input
                        type="email"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        required
                        style={{
                          width: '100%',
                          padding: '14px 16px',
                          border: '2px solid #e0e0e0',
                          borderRadius: '10px',
                          fontSize: '15px',
                          transition: 'all 0.3s',
                          outline: 'none',
                          backgroundColor: '#fafafa'
                        }}
                        placeholder="Enter your registered email"
                        onFocus={(e) => e.target.style.borderColor = '#d32f2f'}
                        onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                      />
                    </div>

                    <button
                      onClick={handleSendOtp}
                      disabled={isLoading}
                      style={{
                        width: '100%',
                        padding: '16px',
                        background: 'linear-gradient(90deg, #d32f2f, #f44336)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '12px',
                        fontSize: '16px',
                        fontWeight: '700',
                        cursor: 'pointer',
                        transition: 'all 0.3s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px',
                        boxShadow: '0 4px 15px rgba(211, 47, 47, 0.3)'
                      }}
                      onMouseOver={(e) => !isLoading && (e.target.style.transform = 'translateY(-2px)')}
                      onMouseOut={(e) => !isLoading && (e.target.style.transform = 'translateY(0)')}
                    >
                      {isLoading ? 'Sending...' : 'Send OTP'} <FaArrowRight />
                    </button>
                  </>
                )}

                {/* Step 2: Verify OTP */}
                {otpSent && !otpVerified && (
                  <>
                    <div style={{ marginBottom: '25px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                        <FaShieldAlt style={{ color: '#d32f2f', marginRight: '10px' }} />
                        <label style={{ color: '#444', fontSize: '14px', fontWeight: '600' }}>
                          Verification Code
                        </label>
                      </div>
                      <input
                        type="text"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        required
                        style={{
                          width: '100%',
                          padding: '14px 16px',
                          border: '2px solid #e0e0e0',
                          borderRadius: '10px',
                          fontSize: '20px',
                          fontWeight: '700',
                          letterSpacing: '12px',
                          textAlign: 'center',
                          transition: 'all 0.3s',
                          outline: 'none',
                          backgroundColor: '#fafafa'
                        }}
                        placeholder="000000"
                        onFocus={(e) => e.target.style.borderColor = '#d32f2f'}
                        onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                      />
                      <p style={{
                        color: '#666',
                        fontSize: '12px',
                        textAlign: 'center',
                        marginTop: '8px'
                      }}>
                        Enter the 6-digit code sent to {forgotEmail}
                      </p>
                    </div>

                    <div style={{ display: 'flex', gap: '15px' }}>
                      <button
                        onClick={handleVerifyOtp}
                        disabled={isLoading}
                        style={{
                          flex: 2,
                          padding: '16px',
                          background: 'linear-gradient(90deg, #d32f2f, #f44336)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '12px',
                          fontSize: '16px',
                          fontWeight: '700',
                          cursor: 'pointer',
                          transition: 'all 0.3s',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '10px',
                          boxShadow: '0 4px 15px rgba(211, 47, 47, 0.3)'
                        }}
                        onMouseOver={(e) => !isLoading && (e.target.style.transform = 'translateY(-2px)')}
                        onMouseOut={(e) => !isLoading && (e.target.style.transform = 'translateY(0)')}
                      >
                        {isLoading ? 'Verifying...' : 'Verify Code'} <FaCheckCircle />
                      </button>
                      <button
                        onClick={handleSendOtp}
                        disabled={isLoading}
                        style={{
                          flex: 1,
                          padding: '16px',
                          backgroundColor: 'white',
                          color: '#d32f2f',
                          border: '2px solid #d32f2f',
                          borderRadius: '12px',
                          fontSize: '16px',
                          fontWeight: '700',
                          cursor: 'pointer',
                          transition: 'all 0.3s',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px'
                        }}
                        onMouseOver={(e) => !isLoading && (e.target.style.backgroundColor = '#ffebee')}
                        onMouseOut={(e) => !isLoading && (e.target.style.backgroundColor = 'white')}
                      >
                        <FaRedo />
                      </button>
                    </div>
                  </>
                )}

                {/* Step 3: Set New Password */}
                {otpVerified && (
                  <>
                    <div style={{ marginBottom: '20px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                        <FaLock style={{ color: '#d32f2f', marginRight: '10px' }} />
                        <label style={{ color: '#444', fontSize: '14px', fontWeight: '600' }}>
                          New Password
                        </label>
                      </div>
                      <input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        style={{
                          width: '100%',
                          padding: '14px 16px',
                          border: '2px solid #e0e0e0',
                          borderRadius: '10px',
                          fontSize: '15px',
                          transition: 'all 0.3s',
                          outline: 'none',
                          backgroundColor: '#fafafa',
                          marginBottom: '15px'
                        }}
                        placeholder="Enter new password"
                        onFocus={(e) => e.target.style.borderColor = '#d32f2f'}
                        onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                      />
                      
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        style={{
                          width: '100%',
                          padding: '14px 16px',
                          border: '2px solid #e0e0e0',
                          borderRadius: '10px',
                          fontSize: '15px',
                          transition: 'all 0.3s',
                          outline: 'none',
                          backgroundColor: '#fafafa'
                        }}
                        placeholder="Confirm new password"
                        onFocus={(e) => e.target.style.borderColor = '#d32f2f'}
                        onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                      />
                    </div>

                    <button
                      onClick={handleResetPassword}
                      disabled={isLoading}
                      style={{
                        width: '100%',
                        padding: '16px',
                        background: 'linear-gradient(90deg, #4caf50, #2e7d32)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '12px',
                        fontSize: '16px',
                        fontWeight: '700',
                        cursor: 'pointer',
                        transition: 'all 0.3s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px',
                        boxShadow: '0 4px 15px rgba(76, 175, 80, 0.3)'
                      }}
                      onMouseOver={(e) => !isLoading && (e.target.style.transform = 'translateY(-2px)')}
                      onMouseOut={(e) => !isLoading && (e.target.style.transform = 'translateY(0)')}
                    >
                      {isLoading ? 'Resetting...' : 'Reset Password'} <FaCheckCircle />
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '20px',
          backgroundColor: '#f8f9fa',
          borderTop: '1px solid #e0e0e0',
          textAlign: 'center'
        }}>
          <p style={{
            color: '#666',
            fontSize: '12px',
            margin: 0
          }}>
            © {new Date().getFullYear()}  All rights reserved by AMAR ENTERPRISES CO. LTD..
          </p>
        </div>
      </div>

      {/* Global Styles */}
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @media (max-width: 768px) {
          .login-card {
            max-width: 90%;
          }
          
          .forgot-password-overlay > div {
            padding: 30px 20px;
          }
        }
        
        @media (max-width: 480px) {
          .login-card {
            max-width: 100%;
          }
          
          .forgot-password-overlay > div {
            padding: 20px 15px;
          }
        }
      `}</style>
    </div>
  );
};

export default AdminLogin;